import { apiSlice } from 'store/slices/api';
import { parseQueryParams } from 'store/slices/utils';
import {
  ClientV2POSTRequest,
  ClientV2POSTResponse,
  ListResponse,
  QueryParams,
} from 'types/api';

import {
  StockEventPOSTSchema,
  StockEventPOSTSpliceCombineResponseSchema,
  StockEventPOSTSpliceCombineSchema,
  StockEventPOSTSplitResponseSchema,
  StockEventPOSTSplitSchema,
  StockEventSchema,
  StockEventUndoPOSTSchema,
} from 'types/clientV2/stock';
import { CLIENT_V2_URLS } from 'store/slices/constants/clientV2';

export const extendedApiSlice = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getStockEventsV2: builder.query<
      ListResponse<StockEventSchema>,
      QueryParams<StockEventSchema>
    >({
      query: (params) =>
        CLIENT_V2_URLS.STOCK_EVENTS(parseQueryParams<StockEventSchema>(params)),
      providesTags: (result) =>
        result
          ? [
              ...result.data.map(
                ({ id }: StockEventSchema) =>
                  ({
                    type: 'ClientV2StockEvents',
                    id,
                  } as const)
              ),
              { type: 'ClientV2StockEvents', id: 'LIST' },
            ]
          : [{ type: 'ClientV2StockEvents', id: 'LIST' }],
    }),
    createStockEvents: builder.mutation<
      ClientV2POSTResponse<StockEventSchema>,
      ClientV2POSTRequest<StockEventPOSTSchema>
    >({
      query: (data) => ({
        url: CLIENT_V2_URLS.CREATE_STOCK_EVENTS,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: (result, error, data) => [
        { type: 'ClientV2StockEvents', id: 'LIST' },
        { type: 'ClientV2HandlingRequests', id: 'LIST' },
        { type: 'HandlingRequests', id: 'LIST' }, // Temporarily included for backwards compatibility
        { type: 'HandlingTaskRows', id: 'LIST' }, // Temporarily included for backwards compatibility
        { type: 'StockLot', id: 'LIST' },
        { type: 'StockLot', id: 'PARTIAL-LIST' },
        {
          type: 'ClientV2HandlingRequests',
          id: Array.isArray(data) ? data[0].stockLot : data.stockLot,
        },
      ],
    }),
    createSplitStockEvent: builder.mutation<
      StockEventPOSTSplitResponseSchema,
      ClientV2POSTRequest<StockEventPOSTSplitSchema>
    >({
      query: (data) => ({
        url: CLIENT_V2_URLS.CREATE_SPLIT_STOCK_EVENT,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: [
        { type: 'ClientV2StockEvents', id: 'LIST' },
        { type: 'ClientV2HandlingRequests', id: 'LIST' },
        { type: 'HandlingRequests', id: 'LIST' }, // Temporarily included for backwards compatibility
        { type: 'HandlingTaskRows', id: 'LIST' }, // Temporarily included for backwards compatibility
        { type: 'StockLot', id: 'LIST' },
        { type: 'StockLot', id: 'PARTIAL-LIST' },
      ],
    }),
    createSpliceCombineStockEvent: builder.mutation<
      StockEventPOSTSpliceCombineResponseSchema,
      ClientV2POSTRequest<StockEventPOSTSpliceCombineSchema>
    >({
      query: (data) => ({
        url: CLIENT_V2_URLS.CREATE_SPLICE_COMBINE_STOCK_EVENT,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: [
        { type: 'ClientV2StockEvents', id: 'LIST' },
        { type: 'ClientV2HandlingRequests', id: 'LIST' },
        { type: 'HandlingRequests', id: 'LIST' }, // Temporarily included for backwards compatibility
        { type: 'HandlingTaskRows', id: 'LIST' }, // Temporarily included for backwards compatibility
        { type: 'StockLot', id: 'LIST' },
        { type: 'StockLot', id: 'PARTIAL-LIST' },
      ],
    }),
    undoStockEvent: builder.mutation<
      ClientV2POSTResponse<StockEventSchema>,
      {
        id: StockEventSchema['id'];
        data: StockEventUndoPOSTSchema;
      }
    >({
      query: ({ id, data }) => ({
        url: CLIENT_V2_URLS.UNDO_STOCK_EVENT(id),
        method: 'POST',
        body: data,
      }),
      invalidatesTags: [
        { type: 'ClientV2HandlingRequests', id: 'LIST' },
        { type: 'ConfirmedPurchaseLines', id: 'LIST' }, // TODO: im only doing this to make sure we refetch purchase lines in receive cause of unreceive, probably better way to do this
        { type: 'HandlingRequests', id: 'LIST' }, // Temporarily included for backwards compatibility
        { type: 'HandlingTaskRows', id: 'LIST' }, // Temporarily included for backwards compatibility
      ],
    }),
  }),
});

export const {
  useGetStockEventsV2Query,
  useCreateStockEventsMutation,
  useCreateSplitStockEventMutation,
  useCreateSpliceCombineStockEventMutation,
  useUndoStockEventMutation,
} = extendedApiSlice;
